import { FileDoneOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Menu, Select, theme } from 'antd';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router';
import { AiSparklesIcon } from '../../../components/icons/AiSparklesIcon.tsx';
import { LoadingIndicator } from '../../../components/LoadingIndicator.tsx';
import type { LabAdminLabQuery } from '../../../graphql/generated/graphql.ts';
import { LabAdminAiSettings } from './LabAdminAiSettings.tsx';
import { LAB_ADMIN_LAB_QUERY } from './LabAdminLab.gql';
import { News } from './News.tsx';
import { Sheets } from './Sheets';

type LabType = NonNullable<LabAdminLabQuery['assignmentForCurrentUser']>['primaryDoctor']['assignedLabs'][number]['lab'];

export const LabAdminLab: React.FC = () => {
  const { token } = theme.useToken();

  const location = useLocation();
  const levels = location.pathname.split('/');
  const level1 = levels[2];

  const [labs, setLabs] = useState<LabType[]>([]);
  const [selectedLab, setSelectedLab] = useState<LabType | null>(null);

  const { data } = useQuery(LAB_ADMIN_LAB_QUERY);

  useEffect(() => {
    if (!data) {
      return;
    }
    setLabs(data?.assignmentForCurrentUser?.primaryDoctor?.assignedLabs?.map(it => it.lab) ?? []);
  }, [data]);

  useEffect(() => {
    if (labs.length && !selectedLab) {
      setSelectedLab(labs[0]);
    }
  }, [labs, selectedLab]);

  if (!selectedLab) {
    return <LoadingIndicator />;
  }

  const styles = {
    wrapper: css`
      display: flex;
      flex-direction: column;
      height: 100%;
    `,
    pageHeader: css`
      border-bottom: 1px solid ${token.colorBorderSecondary};
      background-color: white !important;
      padding: ${token.paddingMD}px ${token.paddingLG}px 0 ${token.paddingLG}px;
    `,
    content: css`
      overflow-y: auto;
      height: 100%;
    `,
  };

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={`${selectedLab.name} (${selectedLab.shortName})`}
        subTitle={selectedLab.longName}
        extra={
          labs.length > 1 ? (
            <Select
              style={{ width: '300px' }}
              options={labs.map(it => ({ label: it.name, value: it.id }))}
              value={selectedLab.id}
              onChange={id => setSelectedLab(labs.find(it => it.id === id)!)}
            />
          ) : null
        }
        className={styles.pageHeader}
      >
        <Menu
          defaultSelectedKeys={['news']}
          selectedKeys={[level1]}
          mode="horizontal"
          style={{ borderBottom: 0, backgroundColor: 'white' }}
          items={[
            {
              key: 'news',
              icon: <InfoCircleOutlined />,
              label: <Link to="../news">News</Link>,
            },
            {
              key: 'sheets',
              icon: <FileDoneOutlined />,
              label: <Link to="../sheets">Sheets</Link>,
            },
            ...(selectedLab.aiEnabled
              ? [
                  {
                    key: 'ai',
                    icon: <AiSparklesIcon size={16} />,
                    label: <Link to="../ai">KI-Konfiguration</Link>,
                  },
                ]
              : []),
          ]}
        />
      </PageHeader>
      <div className={styles.content}>
        <Routes>
          <Route path="news" element={<News labId={selectedLab.id} />} />
          <Route path="sheets" element={<Sheets labId={selectedLab.id} />} />
          <Route path="ai" element={<LabAdminAiSettings labId={selectedLab.id} />} />
          <Route path="*" element={<Navigate replace to="../news" />} />
        </Routes>
      </div>
    </div>
  );
};
